import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Wallet, TrendingUp, TrendingDown, AlertCircle } from "lucide-react";

interface FinanceCardProps {
  title: string;
  value: number;
  type: "income" | "expense" | "balance" | "pending";
  onClick?: () => void;
}

export function FinanceCard({
  title,
  value,
  type,
  onClick
}: FinanceCardProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount);
  };

  const getIcon = () => {
    switch (type) {
      case "income":
        return <TrendingUp className="h-4 w-4" />;
      case "expense":
        return <TrendingDown className="h-4 w-4" />;
      case "pending":
        return <AlertCircle className="h-4 w-4" />;
      case "balance":
        return <Wallet className="h-4 w-4" />;
    }
  };

  const getIconStyles = () => {
    switch (type) {
      case "income":
        return "bg-green-500/10 text-green-600 dark:bg-green-500/20 dark:text-green-400";
      case "expense":
        return "bg-red-500/10 text-red-600 dark:bg-red-500/20 dark:text-red-400";
      case "pending":
        return "bg-red-500/10 text-red-600 dark:bg-red-500/20 dark:text-red-400";
      case "balance":
        return "bg-blue-500/10 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400";
    }
  };

  const getValueColor = () => {
    switch (type) {
      case "income":
        return "text-green-600 dark:text-green-400";
      case "expense":
        return "text-red-600 dark:text-red-400";
      case "pending":
        return "text-red-600 dark:text-red-400";
      case "balance":
        return "text-blue-600 dark:text-green-400";
      default:
        return "text-foreground";
    }
  };

  return (
    <Card 
      className={`bg-card border-border transition-all duration-300 ${
        onClick ? 'cursor-pointer hover:bg-accent/30 hover:shadow-medium' : 'shadow-soft'
      }`} 
      onClick={onClick}
    >
      <CardHeader className="flex flex-row items-center justify-start space-y-0 pb-2 gap-2">
        <div className={`p-2 rounded-xl shadow-soft ${getIconStyles()}`}>
          {getIcon()}
        </div>
        <CardTitle className="text-sm font-medium text-muted-foreground truncate">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className={`text-lg font-bold tabular-nums tracking-tight ${getValueColor()} break-words`}>
          {formatCurrency(value)}
        </div>
      </CardContent>
    </Card>
  );
}